import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNotePalpationCompact.css';

/**
 * Delay between the clinician's final edit and autosave.
 */
const AUTOSAVE_DELAY_MS = 900;

/**
 * Resolve ClinicalNoteMain.entityVersion from a note response.
 */
function resolveEntityVersion(response) {
  if (!response) {
    return null;
  }

  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}

/**
 * Palpation rows are uniquely identified by:
 *
 * palpationTemplateId + measurementSide
 */
function getPalpationKey(row) {
  return (
    `${row?.palpationTemplateId ?? 'unknown'}|` +
    `${row?.measurementSide ?? 'NONE'}`
  );
}

/**
 * Convert nullable backend text into a controlled React value.
 */
function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  return String(value);
}

/**
 * Normalize one PalpationResponse into editable frontend fields.
 */
function normalizePalpationRow(row) {
  return {
    ...row,

    draftPalpationResult:
      toInputValue(
        row?.palpationResult
      ),

    draftComments:
      toInputValue(
        row?.comments
      ),
  };
}

/**
 * Convert backend laterality into clinician-facing text.
 */
function formatSide(side) {
  switch (side) {
    case 'LEFT':
      return 'Left';

    case 'RIGHT':
      return 'Right';

    case 'BILATERAL':
      return 'Bilateral';

    case 'NOT_APPLICABLE':
      return '';

    default:
      return side || '';
  }
}

/**
 * Sort by backend display order, then display name and side.
 */
function sortPalpationRows(rows) {
  return [...rows].sort(
    (first, second) => {
      const firstOrder =
        Number.isFinite(
          Number(
            first?.displayOrder
          )
        )
          ? Number(
              first.displayOrder
            )
          : Number.MAX_SAFE_INTEGER;

      const secondOrder =
        Number.isFinite(
          Number(
            second?.displayOrder
          )
        )
          ? Number(
              second.displayOrder
            )
          : Number.MAX_SAFE_INTEGER;

      if (
        firstOrder !==
        secondOrder
      ) {
        return (
          firstOrder -
          secondOrder
        );
      }

      const nameComparison =
        String(
          first?.displayName ??
            first?.palpationCode ??
            ''
        ).localeCompare(
          String(
            second?.displayName ??
              second?.palpationCode ??
              ''
          )
        );

      if (
        nameComparison !== 0
      ) {
        return nameComparison;
      }

      return String(
        first?.measurementSide ??
          ''
      ).localeCompare(
        String(
          second?.measurementSide ??
            ''
        )
      );
    }
  );
}

/**
 * Palpation result text is intentionally free-form.
 *
 * Required-field completeness is checked by the full clinical-note
 * validation workflow before the note can proceed to review.
 */
function validatePalpationRow() {
  return {};
}

/**
 * Merge a successful save response into the current local row.
 *
 * If the clinician edited the same row while the request was in
 * flight, preserve the newer draft while still refreshing backend
 * metadata.
 */
function mergeSavedPalpationRow(
  currentRow,
  savedResponse,
  preserveDraft
) {
  const normalized =
    normalizePalpationRow(
      savedResponse
    );

  if (!preserveDraft) {
    return normalized;
  }

  return {
    ...normalized,

    draftPalpationResult:
      currentRow
        .draftPalpationResult,

    draftComments:
      currentRow
        .draftComments,
  };
}

/**
 * Previous-visit palpation information.
 */
function PreviousPalpation({
  row,
}) {
  const hasPrevious =
    Boolean(
      row.previousPalpationResult ||
      row.previousComments
    );

  return (
    <div
      style={{
        border:
          '1px solid #e0e3e7',
        borderRadius: 7,
        background:
          '#fafafa',
        padding: 10,
      }}
    >
      <div
        style={{
          marginBottom: 5,
          fontSize: 11,
          fontWeight: 700,
          textTransform:
            'uppercase',
          color: '#777',
        }}
      >
        Previous Visit
      </div>

      {!hasPrevious && (
        <div
          style={{
            fontSize: 12,
            color: '#777',
          }}
        >
          No previous palpation finding
          is available for this area and
          side.
        </div>
      )}

      {hasPrevious && (
        <div
          style={{
            display: 'grid',
            gap: 8,
          }}
        >
          <div>
            <div
              style={{
                marginBottom: 2,
                fontSize: 11,
                fontWeight: 700,
                color: '#666',
              }}
            >
              Finding
            </div>

            <div
              style={{
                fontSize: 13,
                whiteSpace:
                  'pre-wrap',
              }}
            >
              {row.previousPalpationResult ||
                '—'}
            </div>
          </div>

          {row.previousComments && (
            <div>
              <div
                style={{
                  marginBottom: 2,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#666',
                }}
              >
                Comments
              </div>

              <div
                style={{
                  fontSize: 13,
                  whiteSpace:
                    'pre-wrap',
                }}
              >
                {row.previousComments}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One palpation editor card.
 */
function PalpationRow({
  row,
  disabled,
  saveStatus,
  saveError,
  onFieldChange,
  onRetry,
}) {
  const key =
    getPalpationKey(row);

  const side =
    formatSide(
      row.measurementSide
    );

  // Presentation-only controls: do not unmount a draft or alter autosave.
  const [showComments, setShowComments] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [expandedFinding, setExpandedFinding] = useState(false);
  const hasComments = Boolean(String(row.draftComments ?? '').trim());
  const hasPrevious = Boolean(
    String(row.previousPalpationResult ?? '').trim() ||
    String(row.previousComments ?? '').trim()
  );

  const requiredSatisfied =
    !row.required ||
    Boolean(
      row?.draftPalpationResult &&
      String(row.draftPalpationResult).trim()
    );

  let saveStatusText =
    disabled
      ? 'Read only'
      : 'Saved';

  switch (saveStatus) {
    case 'saving':
      saveStatusText =
        'Saving…';
      break;

    case 'unsaved':
      saveStatusText =
        'Unsaved';
      break;

    case 'invalid':
      saveStatusText =
        'Needs correction';
      break;

    case 'error':
      saveStatusText =
        'Autosave failed';
      break;

    default:
      break;
  }

  return (
    <article
      className="clinical-palpation-compact-card"
      style={{
        border:
          '1px solid #ddd',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* ===================================================
          HEADER
          =================================================== */}
      <div
        className="clinical-palpation-compact-header"
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          gap: 12,
          flexWrap: 'wrap',
          padding: 13,
          borderBottom:
            '1px solid #e5e5e5',
          background:
            '#fafafa',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              flexWrap: 'wrap',
            }}
          >
            <strong
              style={{
                fontSize: 15,
              }}
            >
              {row.displayName ||
                row.palpationCode ||
                'Palpation'}
            </strong>

            {side && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#eef3f8',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {side}
              </span>
            )}

            {row.required && (
              <span
                title={
                  requiredSatisfied
                    ? 'Required palpation finding is complete.'
                    : 'Required palpation finding is still incomplete.'
                }
                style={{
                  display: 'inline-block',
                  borderRadius: 999,
                  padding: '2px 7px',
                  background: requiredSatisfied
                    ? '#e8f5e9'
                    : '#fce8e6',
                  color: requiredSatisfied
                    ? '#1f6b2a'
                    : '#8a1c13',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {requiredSatisfied
                  ? 'Required ✓'
                  : 'Required'}
              </span>
            )}
          </div>

          {row.palpationCode && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#666',
              }}
            >
              {row.palpationCode}
            </div>
          )}
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color:
              saveStatus ===
              'error'
                ? '#b42318'
                : saveStatus ===
                    'saving'
                  ? '#555'
                  : '#2e6a35',
          }}
        >
          {saveStatusText}
        </div>
      </div>

      <div
        style={{
          padding: 14,
        }}
      >
        {/* =================================================
            CURRENT FINDING
            ================================================= */}
        <div
          style={{
            display: 'grid',
            gap: 12,
          }}
        >
          <div>
            <label
              htmlFor={`palpation-result-${key}`}
              style={{
                display: 'block',
                marginBottom: 5,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Palpation Finding

              {row.required && (
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 4,
                    color: '#b42318',
                  }}
                >
                  *
                </span>
              )}
            </label>

            <button
              type="button"
              className="clinical-palpation-mini-button"
              aria-expanded={expandedFinding}
              onClick={() => setExpandedFinding((open) => !open)}
            >
              {expandedFinding ? 'Minimize Finding' : 'Expand Finding'}
            </button>
            <textarea
              id={`palpation-result-${key}`}
              rows={expandedFinding ? 7 : 2}
              value={
                row.draftPalpationResult
              }
              disabled={disabled}
              onChange={(event) =>
                onFieldChange(
                  'draftPalpationResult',
                  event.target.value
                )
              }
              placeholder="Enter palpation finding"
              style={{
                width: '100%',
                boxSizing:
                  'border-box',
                resize: 'vertical',
                border:
                  '1px solid #bbb',
                borderRadius: 6,
                padding: 9,
                fontSize: 14,
                lineHeight: 1.45,
                background:
                  disabled
                    ? '#f3f4f6'
                    : '#fff',
              }}
            />
          </div>

          <div className="clinical-palpation-comment-area">
            <button
              type="button"
              className="clinical-palpation-mini-button"
              aria-expanded={showComments}
              onClick={() => setShowComments((open) => !open)}
            >
              {showComments ? 'Hide Comments' : 'Comments'}
              {hasComments && !showComments ? ' • Added' : ''}
            </button>
            {!showComments && hasComments && (
              <div className="clinical-palpation-comment-preview"
                title={row.draftComments}>
                {row.draftComments}
              </div>
            )}
            <div hidden={!showComments}>
              <label htmlFor={`palpation-comments-${key}`}>
                Comments (optional)
              </label>
              <textarea
                id={`palpation-comments-${key}`}
                rows={3}
                value={row.draftComments}
                disabled={disabled}
                onChange={(event) => onFieldChange('draftComments', event.target.value)}
                placeholder="Optional palpation comments"
                className="clinical-palpation-textarea"
              />
            </div>
          </div>
        </div>

        {/* =================================================
            PREVIOUS VISIT
            ================================================= */}
        <div className="clinical-palpation-previous-area">
          <button
            type="button"
            className="clinical-palpation-mini-button"
            aria-expanded={showPrevious}
            onClick={() => setShowPrevious((open) => !open)}
          >
            {showPrevious ? 'Hide Previous Visit' : 'Previous Visit'}
            {hasPrevious && !showPrevious ? ' • Available' : ''}
          </button>
          {showPrevious && <PreviousPalpation row={row} />}
        </div>

        {/* =================================================
            SAVE ERROR
            ================================================= */}
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Palpation finding was not
              saved.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {saveError}
            </div>

            {!disabled && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  marginTop: 8,
                }}
              >
                Retry Save
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Clinical-note Palpation editor.
 *
 * Features:
 *
 * - configured palpation fields visible before first save
 * - fixed backend-controlled laterality
 * - bilateral expansion into LEFT / RIGHT rows
 * - previous-visit comparison
 * - free-text finding and comments
 * - serialized autosave
 * - optimistic-lock parent version refresh
 * - read-only support
 */
export default function ClinicalNotePalpationSection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  const [
    rowStatuses,
    setRowStatuses,
  ] = useState({});

  const [
    rowSaveErrors,
    setRowSaveErrors,
  ] = useState({});

  const [
    editTick,
    setEditTick,
  ] = useState(0);

  const [
    initialized,
    setInitialized,
  ] = useState(false);

  /*
   * Mutable autosave state.
   */
  const rowsRef =
    useRef([]);

  const versionRef =
    useRef(null);

  const rowRevisionRef =
    useRef({});

  const dirtyKeysRef =
    useRef(new Set());

  const failedKeysRef =
    useRef(new Set());

  const savingRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const flushSaveRef =
    useRef(null);

  // Track every clinician edit until its exact revision is confirmed.
  const unsavedKeysRef = useRef(new Set());
  const inFlightSaveRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');

  useEffect(() => {
    rowsRef.current =
      rows;
  }, [rows]);

  useEffect(() => {
    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /**
   * Load:
   *
   * - configured/saved palpation findings
   * - current parent ClinicalNoteMain optimistic version
   */
  useEffect(() => {
    let cancelled = false;

    async function loadPalpation() {
      setLoading(true);
      setLoadError('');
      loadErrorRef.current = '';
      initializedRef.current = false;
      setInitialized(false);
      setRowStatuses({});
      setRowSaveErrors({});

      dirtyKeysRef.current =
        new Set();
      unsavedKeysRef.current = new Set();

      failedKeysRef.current =
        new Set();

      rowRevisionRef.current =
        {};

      savingRef.current =
        false;

      try {
        const [
          palpationResponse,
          noteResponse,
        ] =
          await Promise.all([
            clinicalNoteService
              .getPalpation(
                clinicalNoteId
              ),

            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),
          ]);

        if (cancelled) {
          return;
        }

        const version =
          resolveEntityVersion(
            noteResponse
          );

        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          version;

        const normalizedRows =
          sortPalpationRows(
            (
              palpationResponse ||
              []
            ).map(
              normalizePalpationRow
            )
          );

        const statuses =
          {};

        const revisions =
          {};

        normalizedRows.forEach(
          (row) => {
            const key =
              getPalpationKey(
                row
              );

            statuses[key] =
              'saved';

            revisions[key] =
              0;
          }
        );

        rowsRef.current =
          normalizedRows;

        rowRevisionRef.current =
          revisions;

        setRows(
          normalizedRows
        );

        setRowStatuses(
          statuses
        );

        initializedRef.current = true;
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        loadErrorRef.current = error?.message || 'Palpation findings could not be loaded.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (clinicalNoteId) {
      loadPalpation();
    } else {
      setLoadError(
        'Clinical note ID is required.'
      );

      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  /**
   * Refresh the parent note version after one child autosave.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        const newVersion =
          resolveEntityVersion(
            noteResponse
          );

        if (
          newVersion === null ||
          newVersion === undefined
        ) {
          throw new Error(
            'The palpation finding was saved, but the updated clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          newVersion;

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }

        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );

  /**
   * Update one draft field and mark its palpation row dirty.
   */
  function updateRowField(
    key,
    field,
    value
  ) {
    if (disabled) {
      return;
    }

    const nextRows =
      rowsRef.current.map(
        (row) =>
          getPalpationKey(
            row
          ) === key
            ? {
                ...row,
                [field]: value,
              }
            : row
      );

    rowsRef.current =
      nextRows;

    setRows(
      nextRows
    );

    rowRevisionRef.current = {
      ...rowRevisionRef.current,

      [key]:
        (
          rowRevisionRef
            .current[key] ??
          0
        ) + 1,
    };

    /*
     * A clinician edit permits another autosave attempt after a
     * previously failed request.
     */
    failedKeysRef.current.delete(
      key
    );

    dirtyKeysRef.current.add(
      key
    );
    unsavedKeysRef.current.add(key);

    setRowSaveErrors(
      (current) => ({
        ...current,
        [key]: '',
      })
    );

    const editedRow =
      nextRows.find(
        (row) =>
          getPalpationKey(
            row
          ) === key
      );

    const errors =
      editedRow
        ? validatePalpationRow(
            editedRow
          )
        : {};

    setRowStatuses(
      (current) => ({
        ...current,

        [key]:
          Object.keys(
            errors
          ).length > 0
            ? 'invalid'
            : 'unsaved',
      })
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Apply one successful backend save.
   */
  function applySuccessfulSave(
    key,
    response,
    revisionBeingSaved
  ) {
    const currentRevision =
      rowRevisionRef.current[
        key
      ] ?? 0;

    const preserveDraft =
      currentRevision !==
      revisionBeingSaved;

    const nextRows =
      rowsRef.current.map(
        (row) => {
          if (
            getPalpationKey(
              row
            ) !== key
          ) {
            return row;
          }

          return mergeSavedPalpationRow(
            row,
            response,
            preserveDraft
          );
        }
      );

    rowsRef.current =
      sortPalpationRows(
        nextRows
      );

    setRows(
      rowsRef.current
    );

    if (preserveDraft) {
      dirtyKeysRef.current.add(
        key
      );

      setRowStatuses(
        (current) => ({
          ...current,
          [key]:
            'unsaved',
        })
      );
    } else {
      unsavedKeysRef.current.delete(key);
      setRowStatuses(
        (current) => ({
          ...current,
          [key]:
            'saved',
        })
      );
    }
  }

  /**
   * Save dirty palpation rows sequentially.
   *
   * Each child save updates ClinicalNoteMain.entityVersion, therefore
   * parallel child saves cannot safely share the same expectedVersion.
   */
  const runSaveQueue =
    useCallback(
      async () => {
        if (
          disabled ||
          !initialized ||
          savingRef.current
        ) {
          return;
        }

        savingRef.current =
          true;

        try {
          while (
            mountedRef.current &&
            dirtyKeysRef.current
              .size > 0
          ) {
            const key =
              Array.from(
                dirtyKeysRef.current
              ).find(
                (candidate) =>
                  !failedKeysRef
                    .current
                    .has(candidate)
              );

            if (!key) {
              break;
            }

            /*
             * Remove while this request is active.
             *
             * A later user edit will add the row back to the queue.
             */
            dirtyKeysRef.current.delete(
              key
            );

            const row =
              rowsRef.current.find(
                (candidate) =>
                  getPalpationKey(
                    candidate
                  ) === key
              );

            if (!row) {
              continue;
            }

            const errors =
              validatePalpationRow(
                row
              );

            if (
              Object.keys(
                errors
              ).length > 0
            ) {
              setRowStatuses(
                (current) => ({
                  ...current,
                  [key]:
                    'invalid',
                })
              );

              continue;
            }

            const revisionBeingSaved =
              rowRevisionRef.current[
                key
              ] ?? 0;

            setRowStatuses(
              (current) => ({
                ...current,
                [key]:
                  'saving',
              })
            );

            setRowSaveErrors(
              (current) => ({
                ...current,
                [key]: '',
              })
            );

            try {
              if (
                versionRef.current ===
                  null ||
                versionRef.current ===
                  undefined
              ) {
                await refreshEntityVersion();
              }

              const response =
                await clinicalNoteService
                  .savePalpation(
                    clinicalNoteId,
                    {
                      expectedVersion:
                        versionRef.current,

                      palpationTemplateId:
                        row
                          .palpationTemplateId,

                      measurementSide:
                        row
                          .measurementSide,

                      palpationResult:
                        row
                          .draftPalpationResult,

                      /*
                       * SavePalpationRequest expects "comments".
                       */
                      comments:
                        row
                          .draftComments,
                    }
                  );

              if (
                !mountedRef.current
              ) {
                return;
              }

              /*
               * The parent note version changes after save.
               * Refresh it before processing another palpation row.
               */
              const noteResponse =
                await refreshEntityVersion();

              if (
                !mountedRef.current
              ) {
                return;
              }

              applySuccessfulSave(
                key,
                response,
                revisionBeingSaved
              );

              if (
                typeof onSaved ===
                'function'
              ) {
                onSaved(
                  response,
                  noteResponse
                );
              }
            } catch (error) {
              if (
                !mountedRef.current
              ) {
                return;
              }

              /*
               * Prevent infinite automatic retries.
               *
               * Editing the row or pressing Retry Save re-enables it.
               */
              failedKeysRef.current.add(
                key
              );

              // Keep the key unsaved for the visit-navigation barrier.
              unsavedKeysRef.current.add(key);
              dirtyKeysRef.current.delete(
                key
              );

              setRowStatuses(
                (current) => ({
                  ...current,
                  [key]:
                    'error',
                })
              );

              setRowSaveErrors(
                (current) => ({
                  ...current,

                  [key]:
                    error?.message ||
                    'Palpation finding could not be autosaved.',
                })
              );
            }
          }
        } finally {
          savingRef.current =
            false;

          const hasRetryableDirtyRows =
            Array.from(
              dirtyKeysRef.current
            ).some(
              (key) =>
                !failedKeysRef
                  .current
                  .has(key)
            );

          if (
            mountedRef.current &&
            hasRetryableDirtyRows
          ) {
            window.setTimeout(
              () => {
                flushSaveRef
                  .current?.();
              },
              150
            );
          }
        }
      },
      [
        clinicalNoteId,
        disabled,
        initialized,
        onSaved,
        refreshEntityVersion,
      ]
    );

  // Return the same promise while a save is running so visit navigation
  // cannot proceed merely because savingRef.current was already true.
  const flushSaveQueue = useCallback(() => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current;
    const tracked = Promise.resolve().then(() => runSaveQueue()).finally(() => {
      if (inFlightSaveRef.current === tracked) {
        inFlightSaveRef.current = null;
      }
    });
    inFlightSaveRef.current = tracked;
    return tracked;
  }, [runSaveQueue]);

  // Preserve the existing 900ms debounce and immediate navigation flush.
  flushSaveRef.current = flushSaveQueue;

  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'palpation', {
      flush: async () => {
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        if (dirtyKeysRef.current.size > 0) {
          await flushSaveRef.current?.();
        }
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
      },
      getState: () => {
        const unsaved = [...unsavedKeysRef.current];
        return {
          dirty: unsaved.length > 0 || !initializedRef.current,
          saving: Boolean(inFlightSaveRef.current),
          invalid: unsaved.some((key) => {
            const row = rowsRef.current.find((item) => getPalpationKey(item) === key);
            return row && Object.keys(validatePalpationRow(row)).length > 0;
          }),
          error: loadErrorRef.current ||
            (!initializedRef.current ? 'Palpation is still loading.' : '') ||
            (unsaved.some((key) => failedKeysRef.current.has(key))
              ? 'A Palpation finding failed to save. Correct it or select Retry Save before switching visits.'
              : ''),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);

  /**
   * Debounced autosave trigger.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      dirtyKeysRef.current
        .size === 0
    ) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.();
        },
        AUTOSAVE_DELAY_MS
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editTick,
    initialized,
    disabled,
  ]);

  /**
   * Retry one failed save manually.
   */
  function retryRow(key) {
    if (disabled) {
      return;
    }

    failedKeysRef.current.delete(
      key
    );

    dirtyKeysRef.current.add(
      key
    );

    setRowSaveErrors(
      (current) => ({
        ...current,
        [key]: '',
      })
    );

    setRowStatuses(
      (current) => ({
        ...current,
        [key]:
          'unsaved',
      })
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Overall autosave status for the section.
   */
  const overallStatus =
    useMemo(() => {
      const values =
        Object.values(
          rowStatuses
        );

      if (
        values.includes(
          'saving'
        )
      ) {
        return 'Saving…';
      }

      if (
        values.includes(
          'error'
        )
      ) {
        return 'Autosave error';
      }

      if (
        values.includes(
          'invalid'
        )
      ) {
        return 'Needs correction';
      }

      if (
        values.includes(
          'unsaved'
        )
      ) {
        return 'Unsaved changes';
      }

      return disabled
        ? 'Read only'
        : 'Saved';
    }, [
      rowStatuses,
      disabled,
    ]);

  if (loading) {
    return (
      <section
        style={{
          border:
            '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading palpation findings…
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        style={{
          border:
            '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background:
            '#fff5f5',
        }}
      >
        <strong>
          Palpation section could not
          be loaded
        </strong>

        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }

  return (
    <section
      className="clinical-palpation-compact-section"
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          SECTION HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Palpation
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Document current palpation
            findings and compare them
            with the previous visit.
          </div>
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {overallStatus}
        </div>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        <p className="clinical-palpation-help">
          Laterality is set by the note. Blank optional findings may remain
          unfilled; required findings are checked during final note review.
        </p>

        {rows.length === 0 && (
          <div
            style={{
              border:
                '1px dashed #bbb',
              borderRadius: 8,
              padding: 16,
              color: '#666',
              background:
                '#fafafa',
            }}
          >
            No palpation fields are
            configured for this
            clinical note's body part.
          </div>
        )}

        {rows.length > 0 && (
          <div
            style={{
              display: 'grid',
              gap: 14,
            }}
          >
            {rows.map(
              (row) => {
                const key =
                  getPalpationKey(
                    row
                  );

                return (
                  <PalpationRow
                    key={key}
                    row={row}
                    disabled={
                      disabled
                    }
                    saveStatus={
                      rowStatuses[
                        key
                      ] ||
                      'saved'
                    }
                    saveError={
                      rowSaveErrors[
                        key
                      ] ||
                      ''
                    }
                    onFieldChange={(
                      field,
                      value
                    ) =>
                      updateRowField(
                        key,
                        field,
                        value
                      )
                    }
                    onRetry={() =>
                      retryRow(
                        key
                      )
                    }
                  />
                );
              }
            )}
          </div>
        )}

        {disabled && (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: '#666',
            }}
          >
            Palpation documentation is
            read only because this
            clinical note is no longer
            in the normal Draft editing
            state.
          </div>
        )}
      </div>
    </section>
  );
}